/**
 * Menu lateral: grupos (módulos) e itens, cada item com a permissão que exige. Dados puros,
 * sem tela: o AppShell desenha, os testes conferem.
 */
import type { PermissionCode } from "@cafegestor/shared";

export type AppPath =
  | "/"
  | "/admin/empresa"
  | "/admin/usuarios"
  | "/admin/convites"
  | "/admin/papeis"
  | "/admin/auditoria"
  | "/plataforma"
  | "/cadastros/clientes"
  | "/cadastros/fornecedores"
  | "/cadastros/vendedores"
  | "/cadastros/produtos"
  | "/cadastros/unidades"
  | "/cadastros/formas-pagamento"
  | "/cadastros/tabelas-preco"
  | "/producao/ordens"
  | "/producao/fichas"
  | "/estoque/saldos"
  | "/estoque/movimentacoes"
  | "/estoque/lotes"
  | "/estoque/ajustes"
  | "/estoque/inventarios"
  | "/estoque/depositos"
  | "/estoque/transferencias"
  | "/cargas"
  | "/compras/entradas"
  | "/compras/vinculos"
  | "/vendas/pedidos"
  | "/vendas/venda-direta"
  | "/estoque/pendencias"
  | "/fiscal/acompanhamento"
  | "/fiscal/pendencias"
  | "/financeiro/contas-a-pagar"
  | "/financeiro/contas-a-receber"
  | "/fiscal/documentos"
  | "/fiscal/simulador"
  | "/fiscal/regras"
  | "/fiscal/configuracoes"
  | "/rota"
  | "/vendas/devolucoes";
export type MenuItem = {
  to: AppPath;
  label: string;
  permission?: PermissionCode;
  /** v7: todas estas permissões (ex.: Venda Direta = criar + confirmar + emitir) */
  permissions?: PermissionCode[];
  platform?: boolean;
};
export type MenuGroup = { label: string; items: MenuItem[] };

/**
 * Menu dirigido por permissões: cada item declara a permissão que exige.
 * Um módulo novo entra aqui com a permissão que exige (o teste confere os caminhos).
 */
export const MENU: MenuGroup[] = [
  { label: "Geral", items: [{ to: "/", label: "Início" }] },
  {
    label: "Cadastros",
    items: [
      { to: "/cadastros/clientes", label: "Clientes", permission: "cadastros.ver" },
      { to: "/cadastros/fornecedores", label: "Fornecedores", permission: "cadastros.ver" },
      { to: "/cadastros/vendedores", label: "Vendedores", permission: "cadastros.ver" },
      { to: "/cadastros/produtos", label: "Produtos e embalagens", permission: "cadastros.ver" },
      { to: "/cadastros/unidades", label: "Unidades de medida", permission: "cadastros.ver" },
      {
        to: "/cadastros/formas-pagamento",
        label: "Formas de pagamento",
        permission: "cadastros.ver",
      },
      { to: "/cadastros/tabelas-preco", label: "Tabelas de preço", permission: "cadastros.ver" },
    ],
  },
  {
    label: "Vendas",
    items: [
      { to: "/vendas/pedidos", label: "Pedidos de venda", permission: "vendas.ver" },
      {
        to: "/vendas/venda-direta",
        label: "Venda Direta",
        permissions: ["vendas.pedido.criar", "vendas.pedido.confirmar", "fiscal.documento.emitir"],
      },
      { to: "/cargas", label: "Cargas e acertos", permission: "cargas.ver" },
      { to: "/rota", label: "Venda na rota (celular)", permission: "vendas.rota.confirmar" },
      { to: "/vendas/devolucoes", label: "Devoluções", permission: "vendas.ver" },
    ],
  },
  {
    label: "Fiscal",
    items: [
      { to: "/fiscal/documentos", label: "Documentos fiscais", permission: "fiscal.ver" },
      { to: "/fiscal/acompanhamento", label: "Acompanhamento das notas", permission: "fiscal.ver" },
      { to: "/fiscal/pendencias", label: "Pendências fiscais", permission: "fiscal.ver" },
      { to: "/fiscal/simulador", label: "Simular tributação", permission: "fiscal.ver" },
      { to: "/fiscal/regras", label: "Regras tributárias", permission: "fiscal.ver" },
      {
        to: "/fiscal/configuracoes",
        label: "Configurações fiscais",
        permission: "fiscal.configuracao.editar",
      },
    ],
  },
  {
    label: "Compras",
    items: [
      { to: "/compras/entradas", label: "Entradas de mercadoria", permission: "compras.ver" },
      { to: "/compras/vinculos", label: "Produtos do fornecedor", permission: "compras.ver" },
    ],
  },
  {
    label: "Financeiro",
    items: [
      { to: "/financeiro/contas-a-pagar", label: "Contas a pagar", permission: "financeiro.ver" },
      {
        to: "/financeiro/contas-a-receber",
        label: "Contas a receber",
        permission: "financeiro.ver",
      },
    ],
  },
  {
    label: "Produção",
    items: [
      { to: "/producao/ordens", label: "Ordens de produção", permission: "producao.ver" },
      { to: "/producao/fichas", label: "Fichas técnicas", permission: "producao.ver" },
    ],
  },
  {
    label: "Estoque",
    items: [
      { to: "/estoque/saldos", label: "Saldos", permission: "estoque.ver" },
      { to: "/estoque/pendencias", label: "Pendências de estoque", permission: "estoque.ver" },
      { to: "/estoque/movimentacoes", label: "Movimentações", permission: "estoque.ver" },
      { to: "/estoque/lotes", label: "Lotes e rastreio", permission: "estoque.ver" },
      { to: "/estoque/ajustes", label: "Ajustes", permission: "estoque.ver" },
      { to: "/estoque/inventarios", label: "Inventários", permission: "estoque.ver" },
      { to: "/estoque/transferencias", label: "Transferências", permission: "estoque.ver" },
      { to: "/estoque/depositos", label: "Depósitos", permission: "estoque.ver" },
    ],
  },
  {
    label: "Administração",
    items: [
      { to: "/admin/empresa", label: "Dados da empresa", permission: "admin.empresa.ver" },
      { to: "/admin/usuarios", label: "Usuários", permission: "admin.usuarios.ver" },
      { to: "/admin/convites", label: "Convites", permission: "admin.usuarios.convidar" },
      { to: "/admin/papeis", label: "Papéis e permissões", permission: "admin.usuarios.ver" },
      { to: "/admin/auditoria", label: "Auditoria", permission: "admin.auditoria.ver" },
    ],
  },
  {
    label: "Plataforma",
    items: [{ to: "/plataforma", label: "Organizações e empresas", platform: true }],
  },
];

/** Grupos e itens que a pessoa pode ver (grupo sem nenhum item permitido não aparece). */
export function visibleMenu(opts: {
  isPlatformAdmin: boolean;
  hasCompany: boolean;
  can: (p: PermissionCode) => boolean;
}): MenuGroup[] {
  return MENU.map((g) => ({
    ...g,
    items: g.items.filter((i) =>
      i.platform
        ? opts.isPlatformAdmin
        : i.permissions
          ? i.permissions.every((p) => opts.can(p))
          : i.permission
            ? opts.can(i.permission)
            : opts.hasCompany,
    ),
  })).filter((g) => g.items.length > 0);
}

/** O item do menu é a página atual (ou uma página dentro dela, ex.: /vendas/pedidos/123)? */
export function isCurrentItem(to: string, pathname: string): boolean {
  if (to === "/") return pathname === "/";
  return pathname === to || pathname.startsWith(`${to}/`);
}

/** Grupo da página atual (para abri-lo automaticamente), ou null. */
export function groupOfPath(groups: MenuGroup[], pathname: string): string | null {
  return groups.find((g) => g.items.some((i) => isCurrentItem(i.to, pathname)))?.label ?? null;
}
